"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SignatureDocumentElement = void 0;
const verification_copy_1 = require("../../../../../../constants/verification_copy");
const react_1 = __importDefault(require("react"));
const pro_light_svg_icons_1 = require("@fortawesome/pro-light-svg-icons");
const react_redux_1 = require("react-redux");
const document_1 = require("../../../../../../reducers/slices/document/document");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const constants_1 = require("../../../../../../constants/constants");
const main_1 = require("../../../../../../reducers/slices/main");
const add_signature_1 = require("../add_signature");
const lock_user_1 = __importDefault(require("../lock_user"));
const responsiveSize_1 = require("../../../../../../helpers/responsiveSize");
const update_user_thunk_1 = require("../../../../../../reducers/slices/user/thunks/update_user_thunk");
const get_document_elements_thunk_1 = require("../../../../../../reducers/slices/document/thunks/get/get_document_elements_thunk");
const testHelpers_1 = require("../../../../../../helpers/testHelpers");
const facetecBypass_1 = require("../../../../../../helpers/facetecBypass");
const update_single_element_thunk_1 = require("../../../../../../reducers/slices/document/thunks/update/update_single_element_thunk");
const ui_1 = require("../../../../../../reducers/slices/ui");
const type = "Signature";
// Component definitions must come before the export object
// Note: Scaling is handled by PreviewDocumentElementWrapper - this component renders at 100%
const DocumentComponent = react_1.default.memo(({ elementInstance }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const dispatch = (0, react_redux_1.useDispatch)();
    const { selectedDocument, documentErrors, documentState, selectedDocumentElements, resizeMode } = (0, react_redux_1.useSelector)((state) => state.documentState);
    const { user, accessAuth } = (0, react_redux_1.useSelector)((state) => state.userState);
    const { loginAuthenticated } = (0, react_redux_1.useSelector)((state) => state.uiState);
    const stateCheck = documentState === constants_1.TYPE_LINK;
    const isDraftMode = documentState !== constants_1.TYPE_LINK;
    // Memoize element lookup
    const _element = react_1.default.useMemo(() => { var _a; return (_a = selectedDocumentElements.find((x) => x._id === elementInstance._id)) !== null && _a !== void 0 ? _a : elementInstance; }, [selectedDocumentElements, elementInstance._id, elementInstance]);
    // Field is editable if: no user assigned OR assigned to current user
    // Check both user email, accessAuth user_id (for mobile/access link), AND user._id (for logged-in users)
    const assignedEmail = (_b = (_a = _element === null || _element === void 0 ? void 0 : _element.extra_attributes) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.email;
    const assignedUserId = ((_c = _element === null || _element === void 0 ? void 0 : _element.extra_attributes) === null || _c === void 0 ? void 0 : _c.user_id) || ((_e = (_d = _element === null || _element === void 0 ? void 0 : _element.extra_attributes) === null || _d === void 0 ? void 0 : _d.user) === null || _e === void 0 ? void 0 : _e._id);
    // If no user is assigned, current user IS allowed
    const hasAssignment = !!(assignedEmail || assignedUserId);
    // Check by email (case-insensitive)
    const isCurrentUserByEmail = !!(assignedEmail && (user === null || user === void 0 ? void 0 : user.email) &&
        assignedEmail.toLowerCase() === user.email.toLowerCase());
    // Check by user_id - check BOTH accessAuth (mobile/link) AND user._id (logged-in user)
    const isCurrentUserById = !!(assignedUserId && (((accessAuth === null || accessAuth === void 0 ? void 0 : accessAuth.user_id) && String(assignedUserId) === String(accessAuth.user_id)) ||
        ((user === null || user === void 0 ? void 0 : user._id) && String(assignedUserId) === String(user._id))));
    // In draft mode, document owner can interact with all fields
    const isUser = isDraftMode || !hasAssignment || isCurrentUserByEmail || isCurrentUserById;
    // Document must be in active signing flow (SENT → VIEWED → SIGNING)
    // Allow editing in test mode, with FaceTec authorization, with valid access code, OR with bypass permission
    // In draft mode, fields should always appear interactive (owner can move/configure them)
    const isTestMode = (0, testHelpers_1.isTestModeEnabled)();
    const hasBypass = (0, facetecBypass_1.hasBypassPermission)();
    const isInSigningFlow = (selectedDocument === null || selectedDocument === void 0 ? void 0 : selectedDocument.status) === "SENT" || (selectedDocument === null || selectedDocument === void 0 ? void 0 : selectedDocument.status) === "VIEWED" || (selectedDocument === null || selectedDocument === void 0 ? void 0 : selectedDocument.status) === "SIGNING";
    // Check both component's own logic AND parent wrapper's isEditable prop (if provided)
    // In draft mode, fields should NOT be editable (only configurable via properties sidesheet)
    const parentIsEditable = elementInstance.isEditable !== undefined ? elementInstance.isEditable : true;
    const isEditable = parentIsEditable && !isDraftMode && isInSigningFlow && (isTestMode || loginAuthenticated || !!accessAuth || hasBypass) && isUser;
    // Scaling is handled by PreviewDocumentElementWrapper - this component renders at 100%
    // Use 100% dimensions to fill parent container (which has percentage-based sizing)
    return (react_1.default.createElement("div", { className: "rounded-sm relative", style: {
            width: "100%",
            height: "100%",
            minWidth: (_f = elementInstance.min_width) !== null && _f !== void 0 ? _f : 80,
            minHeight: (_g = elementInstance.min_height) !== null && _g !== void 0 ? _g : 32,
            opacity: !isEditable ? 0.6 : 1,
            cursor: !isUser ? "not-allowed" : "default",
            userSelect: "none",
            WebkitUserSelect: "none",
        }, onClick: (e) => {
            var _a, _b;
            // In draft mode, clicking should select the field for property editing, not trigger signing
            if (isDraftMode) {
                e.stopPropagation();
                dispatch((0, document_1.setSelectedElementID)(_element._id));
                dispatch((0, document_1.setSelectedElement)(_element));
                return;
            }
            // Early return if field is not editable (signing mode only)
            if (!isEditable) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            // Only allow interactions if document is not completed
            if ((selectedDocument === null || selectedDocument === void 0 ? void 0 : selectedDocument.status) !== "COMPLETED") {
                // Check if FaceTec authorization is required for signing
                // Skip FaceTec redirect if in test mode OR if mobile user (already authenticated via app)
                const isMobileUser = (accessAuth === null || accessAuth === void 0 ? void 0 : accessAuth.view_type) === constants_1.VIEW_TYPE.MOBILE;
                if (!loginAuthenticated && !isMobileUser) {
                    if (isTestMode) {
                        // In test mode: Show notice but allow signing to proceed
                        // Certificate will be marked as "Test Mode Bypass"
                        dispatch((0, main_1.setMessageModal)({
                            title: verification_copy_1.TEST_MODE_BYPASS_TITLE,
                            messages: [
                                ...verification_copy_1.TEST_MODE_BYPASS_MESSAGES,
                                "This signing will be marked as 'Test Mode Bypass' in the certificate.",
                            ],
                        }));
                        // Don't return - allow signing to proceed after showing notice
                    }
                    else {
                        // Not in test mode: Show auth method selection (QR or FaceTec)
                        dispatch((0, ui_1.showQrAuth)());
                        return;
                    }
                }
                if (isUser) {
                    dispatch((0, main_1.setContentModal)({
                        title: `Add Signature`,
                        content: (react_1.default.createElement(add_signature_1.AddSignature, { value: (_b = (_a = _element === null || _element === void 0 ? void 0 : _element.extra_attributes) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : user === null || user === void 0 ? void 0 : user.signature, onClick: (image) => {
                                dispatch((0, update_single_element_thunk_1.update_single_element_thunk)({
                                    data: {
                                        document_id: selectedDocument === null || selectedDocument === void 0 ? void 0 : selectedDocument._id,
                                        payload: Object.assign(Object.assign({}, _element), { extra_attributes: Object.assign(Object.assign({}, _element.extra_attributes), { value: image }) }),
                                    },
                                })).then((res) => {
                                    if (res.payload) {
                                        dispatch((0, update_user_thunk_1.update_user_thunk)({ data: { signature: image } }));
                                        dispatch((0, get_document_elements_thunk_1.get_document_elements_thunk)({ data: { document_id: selectedDocument._id } }));
                                    }
                                });
                            } })),
                    }));
                }
                else {
                    dispatch((0, main_1.setMessageModal)({
                        title: "Field Not Assigned",
                        messages: ["This signature field is assigned to another signer. You cannot sign it."],
                    }));
                }
            }
        } }, ((_h = _element === null || _element === void 0 ? void 0 : _element.extra_attributes) === null || _h === void 0 ? void 0 : _h.use_my_signature) || (((_j = _element === null || _element === void 0 ? void 0 : _element.extra_attributes) === null || _j === void 0 ? void 0 : _j.value) && stateCheck) ? (react_1.default.createElement("img", { src: (_k = _element === null || _element === void 0 ? void 0 : _element.extra_attributes) === null || _k === void 0 ? void 0 : _k.value, style: { width: "100%", height: "100%", objectFit: "contain" } })) : (react_1.default.createElement("div", { className: `flex gap-2 justify-center border-dashed border ${documentErrors.find((x) => x === _element._id) ? "border-error" : "border-superDark"} bg-white items-center`, style: {
            padding: "8px",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
        } },
        react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faSignature, className: "font-bold text-black", style: { fontSize: `${Math.min((_l = elementInstance.height) !== null && _l !== void 0 ? _l : 48, 32)}px` } }),
        !constants_1.IS_MOBILE && ((_m = elementInstance.width) !== null && _m !== void 0 ? _m : 144) > 100 && (react_1.default.createElement("div", { className: "relative top-0.5" },
            react_1.default.createElement("span", { className: "text-black", style: { fontSize: "14px" } }, "Signature")))))));
});
function SideBarComponent({ elementInstance }) {
    var _a, _b;
    const dispatch = (0, react_redux_1.useDispatch)();
    const { selectedDocument, selectedDocumentElements } = (0, react_redux_1.useSelector)((state) => state.documentState);
    const { user } = (0, react_redux_1.useSelector)((state) => state.userState);
    // Memoize element lookup to prevent unnecessary re-lookups
    const _element = react_1.default.useMemo(() => { var _a; return (_a = selectedDocumentElements.find((x) => x._id === elementInstance._id)) !== null && _a !== void 0 ? _a : elementInstance; }, [selectedDocumentElements, elementInstance._id, elementInstance]);
    return (react_1.default.createElement("div", { className: "flex flex-col gap-4" },
        react_1.default.createElement(lock_user_1.default, { userProp: (_b = (_a = _element === null || _element === void 0 ? void 0 : _element.extra_attributes) === null || _a === void 0 ? void 0 : _a.user) !== null && _b !== void 0 ? _b : null, onClick: (user) => {
                var _a;
                // Get fresh element state from Redux to avoid stale data
                const latestElement = (_a = selectedDocumentElements.find((x) => x._id === elementInstance._id)) !== null && _a !== void 0 ? _a : elementInstance;
                const updatedElement = Object.assign(Object.assign({}, latestElement), { extra_attributes: Object.assign(Object.assign({}, latestElement.extra_attributes), { user, user_id: user === null || user === void 0 ? void 0 : user._id, restricted: !!user }) });
                // Update Redux immediately (show label instantly)
                dispatch((0, document_1.editSelectedDocumentElements)(updatedElement));
                // Queue for batched save
                dispatch((0, document_1.addPendingUpdate)({
                    elementId: elementInstance._id,
                    changes: {
                        extra_attributes: {
                            user,
                            user_id: user === null || user === void 0 ? void 0 : user._id,
                            restricted: !!user,
                        },
                    },
                }));
            } })));
}
// Export object (must come after component definitions)
exports.SignatureDocumentElement = {
    type,
    sideBarComponent: SideBarComponent,
    documentComponent: DocumentComponent,
    propertiesComponent: () => react_1.default.createElement("div", null, "Properties Components"),
    construct: (_id, page) => {
        return {
            _id,
            type,
            page,
            scaling_mode: "custom",
            width: (0, responsiveSize_1.getResponsiveSize)(144, "width"),
            height: (0, responsiveSize_1.getResponsiveSize)(50, "height"),
            min_width: (0, responsiveSize_1.getResponsiveSize)(80, "width"),
            min_height: (0, responsiveSize_1.getResponsiveSize)(32, "height"),
            extra_attributes: {
                value: "",
                use_my_signature: false,
                user: null,
                edit_type: "fillable",
                x: 0,
                y: 0,
            },
        };
    },
    sidebarBtnElement: {
        icon: pro_light_svg_icons_1.faSignature,
        label: "Signature",
    },
};
